/**
 * Where the reader is, kept in the address bar.
 *
 * The app had no router and did not need one while every screen was reached by clicking:
 * each shell held its own position in `useState` and nothing outside it had to know. A page
 * refresh is what broke that. The position started at the default on every mount, so a lead
 * auditor who refreshed on an audit's report step was returned to the audits list — with a
 * session that was still perfectly good, which made it look like a sign-out.
 *
 * This is not a router library and does not grow into one. It is the browser's own history
 * API with two things added: a hook that re-renders when the location changes, and a
 * `navigate` that announces a push. Each role encodes *its own* view union into a path and
 * reads it back, so the union stays the only list of screens there is — a route table here
 * would be a second one, and the two would disagree within a milestone.
 *
 * `nginx.conf` has served this shape since before there was anything to serve: "a hard
 * refresh on /audits/<id>/plan has to return index.html".
 */

import { useEffect, useState } from "react";

/** `pushState` and `replaceState` deliberately fire nothing, so a move announces itself. */
const MOVED = "complao:navigated";

/**
 * Go to a path.
 *
 * `replace` is for a correction rather than a move — arriving at `/` and being put at
 * `/audits` is not a place the back button should be able to return to, because it was
 * never a screen.
 */
export function navigate(path: string, replace = false): void {
  if (path === location.pathname) return;
  if (replace) history.replaceState(null, "", path);
  else history.pushState(null, "", path);
  window.dispatchEvent(new Event(MOVED));
}

/** The current path, re-read on the browser's own back and forward as well as on ours. */
export function usePath(): string {
  const [path, setPath] = useState(() => location.pathname);

  useEffect(() => {
    const read = () => setPath(location.pathname);
    window.addEventListener("popstate", read);
    window.addEventListener(MOVED, read);
    // The location can move between the first render and this effect — a redirect during
    // another component's mount — and the state would otherwise hold the older one.
    read();
    return () => {
      window.removeEventListener("popstate", read);
      window.removeEventListener(MOVED, read);
    };
  }, []);

  return path;
}

/**
 * The address bar's fragment, re-read whenever it moves.
 *
 * The third thing this module adds to the history API, and the reason is that a fragment
 * navigation is a **same-document** navigation: pasting a second link over a first changes
 * the address bar and reloads nothing. A token read once at mount is then the token that
 * stays, and the four mailed capabilities were all read exactly once — so a fresh
 * set-password link pasted into a tab already showing a spent one put the new token in the
 * address bar and left the old one's *this link has already been used* on the screen. The
 * refusal was true of a token nobody was looking at.
 *
 * `hashchange` alone is not enough. It does not fire when the path changes with it, and it
 * does not fire for our own `navigate`, so the two the path already listens on are here too.
 */
export function useFragment(): string {
  const [fragment, setFragment] = useState(() => location.hash);

  useEffect(() => {
    const read = () => setFragment(location.hash);
    window.addEventListener("hashchange", read);
    window.addEventListener("popstate", read);
    window.addEventListener(MOVED, read);
    // As `usePath` does, and for the same reason: the location can move between the first
    // render and this effect.
    read();
    return () => {
      window.removeEventListener("hashchange", read);
      window.removeEventListener("popstate", read);
      window.removeEventListener(MOVED, read);
    };
  }, []);

  return fragment;
}

/** A path's segments, empty ones dropped: `/audits/9f3c/` gives `["audits", "9f3c"]`. */
export function segments(path: string): readonly string[] {
  return path.split("/").filter(Boolean);
}
